import { useEffect, useState } from "react";
import { adminApi, extractApiError } from "../../lib/adminApi";
import {
    AdminPageHeader,
    SectionCard,
} from "../../components/admin/FormPrimitives";
import { Mail, Trash2, Check, ArrowLeft } from "lucide-react";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString("fr-CA", {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

function StatusBadge({ status }) {
    const map = {
        pending_key: ["En attente Brevo", "text-neutral-400 border-neutral-600"],
        sent: ["Envoyé", "text-emerald-300 border-emerald-800"],
        failed: ["Échec", "text-red-300 border-red-700"],
    };
    const [label, cls] = map[status] || [status, "text-neutral-400 border-neutral-600"];
    return (
        <span className={`text-[0.65rem] tracking-widest uppercase px-2 py-0.5 border ${cls}`}>
            {label}
        </span>
    );
}

export default function AdminInbox() {
    const [messages, setMessages] = useState([]);
    const [selected, setSelected] = useState(null);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("all"); // all | unread

    const reload = async () => {
        try {
            const { data } = await adminApi.get(
                `/admin/messages${filter === "unread" ? "?unread_only=true" : ""}`,
            );
            setMessages(data || []);
            // Notify sidebar so the unread badge stays fresh.
            window.dispatchEvent(new CustomEvent("admin:messages-updated"));
        } catch (err) {
            setError(extractApiError(err));
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const markRead = async (m) => {
        try {
            await adminApi.patch(`/admin/messages/${m.id}/read`);
            await reload();
            if (selected?.id === m.id) setSelected({ ...m, read: true });
        } catch (err) {
            setError(extractApiError(err));
        }
    };

    const remove = async (m) => {
        if (!window.confirm("Supprimer ce message ?")) return;
        try {
            await adminApi.delete(`/admin/messages/${m.id}`);
            if (selected?.id === m.id) setSelected(null);
            await reload();
        } catch (err) {
            setError(extractApiError(err));
        }
    };

    return (
        <div data-testid="admin-inbox-page">
            <AdminPageHeader
                title="Boîte de réception"
                subtitle="Messages reçus depuis le formulaire d'accueil et la page contact. Le routage vers cabinet.mabs5 ou mesanou.mab est indiqué."
            />
            <div className="mb-5 flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setFilter("all")}
                    className={`px-3 py-2 text-sm border transition-colors ${
                        filter === "all"
                            ? "border-gold text-gold"
                            : "border-ink-border text-neutral-300 hover:border-ink-borderStrong"
                    }`}
                    data-testid="inbox-filter-all"
                >
                    Tous
                </button>
                <button
                    type="button"
                    onClick={() => setFilter("unread")}
                    className={`px-3 py-2 text-sm border transition-colors ${
                        filter === "unread"
                            ? "border-gold text-gold"
                            : "border-ink-border text-neutral-300 hover:border-ink-borderStrong"
                    }`}
                    data-testid="inbox-filter-unread"
                >
                    Non lus
                </button>
                {error && (
                    <span className="ml-3 text-sm text-red-300">{error}</span>
                )}
            </div>

            {selected ? (
                <SectionCard
                    title={selected.subject || "(sans objet)"}
                    testId="inbox-message-detail"
                >
                    <button
                        type="button"
                        onClick={() => setSelected(null)}
                        className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-gold mb-5 transition-colors"
                        data-testid="inbox-back"
                    >
                        <ArrowLeft size={14} /> Retour à la liste
                    </button>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-6">
                        <div>
                            <dt className="eyebrow mb-1">Reçu le</dt>
                            <dd className="text-neutral-200">
                                {formatDate(selected.created_at)}
                            </dd>
                        </div>
                        <div>
                            <dt className="eyebrow mb-1">Source</dt>
                            <dd className="text-neutral-200">
                                {selected.source === "home"
                                    ? "Formulaire accueil"
                                    : "Page contact"}
                            </dd>
                        </div>
                        <div>
                            <dt className="eyebrow mb-1">Domaine</dt>
                            <dd className="text-neutral-200">
                                {selected.domain || "—"}
                            </dd>
                        </div>
                        <div>
                            <dt className="eyebrow mb-1">Envoyé à</dt>
                            <dd className="text-neutral-200">
                                {selected.routed_to}{" "}
                                <StatusBadge status={selected.email_status} />
                            </dd>
                        </div>
                        <div>
                            <dt className="eyebrow mb-1">Expéditeur</dt>
                            <dd className="text-neutral-200">
                                {selected.name || "—"}
                            </dd>
                        </div>
                        <div>
                            <dt className="eyebrow mb-1">Courriel</dt>
                            <dd className="text-neutral-200 break-all">
                                <a
                                    href={`mailto:${selected.email}`}
                                    className="hover:text-gold"
                                >
                                    {selected.email}
                                </a>
                            </dd>
                        </div>
                        {selected.phone && (
                            <div>
                                <dt className="eyebrow mb-1">Téléphone</dt>
                                <dd className="text-neutral-200">
                                    <a
                                        href={`tel:${selected.phone.replace(/\s+/g, "")}`}
                                        className="hover:text-gold"
                                    >
                                        {selected.phone}
                                    </a>
                                </dd>
                            </div>
                        )}
                    </dl>
                    <div
                        className="border-l-2 border-gold/50 pl-5 whitespace-pre-wrap text-neutral-200"
                        data-testid="inbox-message-body"
                    >
                        {selected.message}
                    </div>
                    <div className="mt-6 flex items-center gap-3 flex-wrap">
                        {!selected.read && (
                            <button
                                type="button"
                                onClick={() => markRead(selected)}
                                className="btn-outline-gold"
                                data-testid="inbox-mark-read"
                            >
                                <Check size={14} /> Marquer lu
                            </button>
                        )}
                        <a
                            href={`mailto:${selected.email}?subject=${encodeURIComponent("Re: " + (selected.subject || ""))}`}
                            className="btn-outline-gold"
                            data-testid="inbox-reply"
                        >
                            <Mail size={14} /> Répondre
                        </a>
                        <button
                            type="button"
                            onClick={() => remove(selected)}
                            className="text-sm text-neutral-400 hover:text-red-400 px-3 py-2 border border-ink-border hover:border-red-500 inline-flex items-center gap-2 transition-colors"
                            data-testid="inbox-delete"
                        >
                            <Trash2 size={14} /> Supprimer
                        </button>
                    </div>
                </SectionCard>
            ) : (
                <SectionCard
                    title={`Messages (${messages.length})`}
                    testId="inbox-list"
                >
                    {messages.length === 0 ? (
                        <div className="text-neutral-400 text-sm">
                            Aucun message pour l'instant.
                        </div>
                    ) : (
                        <div className="divide-y divide-ink-border">
                            {messages.map((m) => (
                                <button
                                    type="button"
                                    key={m.id}
                                    onClick={() => {
                                        setSelected(m);
                                        if (!m.read) markRead(m);
                                    }}
                                    data-testid={`inbox-row-${m.id}`}
                                    className={`w-full text-left py-4 flex items-start gap-4 hover:bg-ink-surface/40 transition-colors px-2 ${
                                        !m.read ? "bg-ink-surface/30" : ""
                                    }`}
                                >
                                    <div className="w-2 h-2 mt-2 shrink-0 rounded-full bg-gold" style={{ opacity: m.read ? 0 : 1 }} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            <span
                                                className={`font-serif text-base ${
                                                    !m.read
                                                        ? "text-neutral-50"
                                                        : "text-neutral-300"
                                                } truncate`}
                                            >
                                                {m.name ||
                                                    m.email ||
                                                    "(anonyme)"}
                                            </span>
                                            <span className="text-xs text-neutral-500">
                                                {formatDate(m.created_at)}
                                            </span>
                                            <StatusBadge
                                                status={m.email_status}
                                            />
                                        </div>
                                        <div className="text-sm text-neutral-400 truncate mt-1">
                                            {m.subject || m.message?.slice(0, 100)}
                                        </div>
                                        <div className="text-xs text-neutral-500 mt-1">
                                            → {m.routed_to}
                                            {m.domain
                                                ? ` · ${m.domain}`
                                                : ""}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </SectionCard>
            )}
        </div>
    );
}
